import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { NAV_LINKS, BRAND } from "@/lib/brand";
import UserMenu from "@/components/UserMenu";
import NotificationsBell from "@/components/NotificationsBell";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.href.replace("#", ""));
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-brand-link" className="flex items-center gap-3 group shrink-0">
          <div className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-[#D4AF37]/40 bg-black shadow-[0_0_18px_rgba(212,175,55,0.35)] group-hover:shadow-[0_0_28px_rgba(212,175,55,0.6)] transition-shadow">
            <img src={BRAND.logoUrl} alt="TGL" className="w-full h-full object-contain" />
          </div>
          <span className="brand-title font-display text-sm font-bold tracking-[0.25em] text-white whitespace-nowrap hidden sm:inline">
            THE GOLDEN LEGENDS
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => {
            const id = l.href.replace("#", "");
            const isActive = active === id;
            return (
              <a key={l.label} href={`/${l.href}`}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                data-active={isActive ? "true" : "false"}
                className={`nav-link text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "nav-link-active text-[#FFE9A0]" : "text-zinc-400 hover:text-[#D4AF37]"
                }`}
              >
                {l.label}
              </a>
            );
          })}
          <Link to="/teams" data-testid="nav-link-teams"
            className="nav-link text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 hover:text-[#D4AF37] transition-colors">
            Teams
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <NotificationsBell />
          <UserMenu />
          <button data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-black/95 border-t border-white/10">
          <div className="flex flex-col px-6 py-4 gap-4">
            {NAV_LINKS.map((l) => {
              const id = l.href.replace("#", "");
              const isActive = active === id;
              return (
                <a key={l.label} href={`/${l.href}`} onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={`text-sm font-bold uppercase tracking-widest ${
                    isActive ? "nav-link-active text-[#FFE9A0]" : "text-zinc-300 hover:text-[#D4AF37]"
                  }`}
                >
                  {l.label}
                </a>
              );
            })}
            <Link to="/teams" onClick={() => setOpen(false)}
              className="text-sm font-bold uppercase tracking-widest text-zinc-300 hover:text-[#D4AF37]">
              Teams
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
